import { useQuery } from '@tanstack/react-query'
import { Constants } from 'constants/constants'
import { QueryKeys } from 'constants/query-keys'
import { fetchPayrollLocationWeeklyData } from 'queries/payroll/location/location'
import { PayrollFetchProps } from 'types/api'
import { IResponse, apiNotEnabled } from 'utils/api'
import {
    getGreenfieldDrillValues,
    getGreenfieldLocationFilter,
} from 'utils/greenfield'
import { isStore } from 'utils/location'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'

interface PayrollLocationWeeklyFetchProps extends PayrollFetchProps {
    timeDimension?: string
}

export function usePayrollLocationWeekly({
    env,
    accessToken,
    location,
    timeDimension = 'this_month',
}: PayrollLocationWeeklyFetchProps): IResponse {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME
    const enabled = isStore(location)
    const greenfieldCardNumber =
        Constants.GREENFIELD_CARDS.PAYROLL.LOCATION_WEEKLY
    // GF card 532905 does not support time_dimension=this_month —
    // omitting the param returns current-month data by default.
    // Only pass time_dimension when requesting a different period (e.g. prior_month, next_month).
    const greenfieldFilters: { [key: string]: string } = {
        ...getGreenfieldDrillValues(location),
        ...getGreenfieldLocationFilter(location),
    }
    if (timeDimension && timeDimension !== 'this_month') {
        greenfieldFilters.time_dimension = timeDimension
    }

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.PAYROLL_LOCATION_WEEKLY, location, timeDimension],
        () =>
            fetchPayrollLocationWeeklyData({
                env,
                accessToken,
                dynamicSubPath: greenfieldCardNumber,
                filters: greenfieldFilters,
                trackEvent,
            }),
        {
            enabled,
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
        },
    )

    if (!enabled) {
        return apiNotEnabled()
    }

    return { data, isLoading, isError, refetch, remove, error }
}
